import React, { useState, useEffect } from 'react'
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Divider,
  Chip,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Microsoft,
  Code,
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import backgroundImage from '../assets/izzi-background.jpg'
import agentFlowLogo from '../assets/AgentFlowIZZI.svg'
import { keyframes } from '@mui/system'

// Define fade animation
const fadeInOut = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.3;
  }
`

const LoginPage = () => {
  const { login, isDevelopmentAuth } = useAuth()
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [countdown, setCountdown] = useState(5)

  // Check for access denied message on component mount
  useEffect(() => {
    const accessDeniedMessage = localStorage.getItem('agentflow_access_denied')
    if (accessDeniedMessage) {
      setError(accessDeniedMessage)
      localStorage.removeItem('agentflow_access_denied') // Clear the message after displaying
    }
  }, [])

  // Auto-login effect for production mode
  useEffect(() => {
    if (!isDevelopmentAuth && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (!isDevelopmentAuth && countdown === 0) {
      handleAzureLogin()
    }
  }, [countdown, isDevelopmentAuth])

  const handleAzureLogin = async () => {
    setLoading(true)
    setError('')

    try {
      await login()
    } catch (error) {
      console.error('Azure login error:', error)
      setError('Azure AD authentication failed. Please try again.')
      // Reset countdown on error to allow manual retry
      setCountdown(-1)
    } finally {
      setLoading(false)
    }
  }

  const handleTokenSubmit = async (e) => {
    e.preventDefault()
    
    if (!token.trim()) {
      setError('Please enter a JWT token')
      return
    }

    setLoading(true)
    setError('')

    try {
      await login(token.trim())
    } catch (error) {
      console.error('Token login error:', error)
      setError('Authentication failed. Please check your token and try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        p: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)', // Dark overlay for better readability
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <Box
          sx={{
            p: 4,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src={agentFlowLogo}
              alt="AgentFlow"
              style={{
                maxWidth: '400px',
                width: '100%',
                height: 'auto',
                filter: 'brightness(0.8)',
                animation: !isDevelopmentAuth && countdown >= 0 ? `${fadeInOut} 3s ease-in-out infinite` : 'none',
                dropShadow: '0 0 20px rgba(176, 176, 176, 0.3)',
              }}
            />
            {!isDevelopmentAuth && countdown >= 0 && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 3,
                    color: '#b0b0b0',
                    fontWeight: 400,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontSize: '0.9rem'
                  }}
                >
                  Summoning your session
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.3, mt: 1 }}>
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 8,
                        height: 8,
                        backgroundColor: '#b0b0b0',
                        borderRadius: '50%',
                        animation: `typingDot 1.4s infinite ease-in-out`,
                        animationDelay: `${i * 0.16}s`,
                        '@keyframes typingDot': {
                          '0%, 80%, 100%': {
                            transform: 'scale(0.8)',
                            opacity: 0.5,
                          },
                          '40%': {
                            transform: 'scale(1)',
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
            {isDevelopmentAuth && (
              <Chip
                icon={<Code />}
                label="Development Mode"
                color="warning"
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          {/* Azure AD Login (Production Mode) */}
          {!isDevelopmentAuth && (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(211, 47, 47, 0.1)' }}>
                  {error}
                </Alert>
              )}
              {/* Only show the button when auto-summon isn't running (countdown < 0 means failed/reset) */}
              {countdown < 0 && (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<Microsoft />}
                  onClick={handleAzureLogin}
                  disabled={loading}
                  sx={{
                    mb: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    bgcolor: '#0078d4',
                    '&:hover': {
                      bgcolor: '#106ebe',
                    },
                  }}
                >
                  {loading ? 'Authenticating...' : 'Sign in with Microsoft'}
                </Button>
              )}
            </>
          )}

          {/* Manual Token Login (Development Mode only) */}
          {isDevelopmentAuth && (
            <form onSubmit={handleTokenSubmit}>
              <Typography variant="body2" sx={{ mb: 1, color: '#b0b0b0' }}>
                Enter development JWT token:
              </Typography>
              
              <TextField
                fullWidth
                label="JWT Token"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                error={!!error}
                disabled={loading}
                multiline={showPassword}
                rows={showPassword ? 4 : 1}
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    color: '#b0b0b0',
                    '& fieldset': {
                      borderColor: 'rgba(176, 176, 176, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(176, 176, 176, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#b0b0b0',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(176, 176, 176, 0.8)',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#b0b0b0' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(211, 47, 47, 0.1)' }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mb: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  bgcolor: '#64b5f6',
                  '&:hover': {
                    bgcolor: '#42a5f5',
                  },
                }}
              >
                {loading ? 'Authenticating...' : 'Sign In with Token'}
              </Button>
            </form>
          )}
        </Box>
      </Container>
    </Box>
  )
}

export default LoginPage